'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';
import { Chip, Tooltip } from '@mui/material';

import MasterSizeAddEdit from './MasterSizeAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useTireSizeDataList, deleteTireSize } from '../../../../stores/tireSizeStore';
import { fleetApi } from '../../../../api/axiosClient';
import { useCompanyList } from '../../../../stores/companyStore';
import { useVehicleTypeList } from '../../../../stores/vehicleTypeStore';

function MasterSizePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('code');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const { companyOptions } = useCompanyList();

  const { data: vehicleTypesResp } = useVehicleTypeList(0, 1000, '', '');
  const vehicleTypes = useMemo(() => {
    return Array.isArray(vehicleTypesResp) ? vehicleTypesResp : [];
  }, [vehicleTypesResp]);

  const getLabelNameVehicleType = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids.map((id) => {
      const found = vehicleTypes.find((v) => String(v.id) === String(id));
      return found ? found.name || String(id) : String(id);
    });
  };

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );
  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const searchFieldOptions = useMemo(() => [{ value: 'code', label: t('size.code', 'Size Code') }], [t]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useTireSizeDataList(paginationModel.page, paginationModel.pageSize, selectedCompanies, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      const fetchFilters = [];

      if (searchRaw && searchRaw.trim() !== '') {
        if (searchField === 'code' || searchField === 'code_internal') {
          fetchFilters.push(
            { name: 'code', logic_operator: 'like', operator: 'OR', value: searchRaw.trim() },
            { name: 'code_internal', logic_operator: 'like', operator: 'OR', value: searchRaw.trim() }
          );
        } else if (searchField === 'all') {
          fetchFilters.push(
            { name: 'code', value: searchRaw },
            { name: 'code_internal', value: searchRaw },
            { name: 'name', table_name: 'm_company', value: searchRaw },
            { name: 'name', table_name: 'm_vehicle_type', value: searchRaw }
          );
        } else {
          if (searchField === 'company') {
            fetchFilters.push({ name: 'name', table_name: 'm_company', logic_operator: 'like', operator: 'AND', value: searchRaw.trim() });
          } else if (searchField === 'vehicle_type') {
            fetchFilters.push({
              name: 'name',
              table_name: 'm_vehicle_type',
              logic_operator: 'like',
              operator: 'AND',
              value: searchRaw.trim()
            });
          } else {
            fetchFilters.push({ name: searchField, logic_operator: 'like', operator: 'AND', value: searchRaw.trim() });
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['code'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'vehicle_type', column_results: ['name'] }
        ],
        columns: columns
      };

      if (fetchFilters.length > 0) {
        if (searchField === 'all') {
          payload.filters = fetchFilters;
        } else {
          payload.columns = [...payload.columns, ...fetchFilters];
        }
      }

      const companies = (selectedCompanies || []).map((c) => c.value).join(',');
      if (companies) {
        payload.columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: companies });
      }

      const resp = await fleetApi.post('/tire-size/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('tire size export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleCompanyChange = (companies) => {
    setSelectedCompanies(companies);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTireSize(confirmTargetId);
      notify({ message: t('size.notifSuccess', 'Size successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('size.notifFailed', 'Failed to delete Size'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1,
      valueGetter: (value, row) => row?.join?.company_name || row?.company_name || '-'
    },
    {
      field: 'code',
      headerName: t('size.code', 'Size Code'),
      flex: 1,
      valueGetter: (value, row) => row?.code_internal || row?.code || '-'
    },
    {
      field: 'application',
      headerName: t('size.application', 'Application'),
      flex: 1.5,
      renderCell: (params) => {
        const val = params.row?.join?.vehicle_type_name || params.row?.vehicle_type_name;
        const ids = params.row?.vehicle_type_ids;

        let names = [];
        if (Array.isArray(ids) && ids.length > 0) {
          names = getLabelNameVehicleType(ids);
        } else if (Array.isArray(val)) {
          names = val;
        } else if (typeof val === 'string') {
          names = val
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        }

        if (names.length === 0) return '-';
        return (
          <Tooltip
            title={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {names.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            }
            arrow
            placement="top"
          >
            <Chip
              label={`${names.length} Application${names.length > 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'status',
      headerName: t('masterCompany.status', 'Status'),
      flex: 0.8,
      renderCell: (params) => {
        const isActive = params.row.status === 'Active';
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'default'}
            size="small"
            sx={{
              fontWeight: 600,
              minWidth: 70,
              backgroundColor: isActive ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.08)',
              color: isActive ? '#2e7d32' : 'text.secondary'
            }}
          />
        );
      }
    },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.code)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('size.title', 'Master Size')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.search', 'Search...')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && <MasterSizeAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterSizePage() {
  return <MasterSizePageContent />;
}
